import React, { useState, createContext, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';

import { useSettingsContext } from '@mono/platmap-settings-context';
import { diff } from '@mono/diff';
import { Layer, PlatMapSettings, WXTilesLayer, PlatmapLayerContext } from '@mono/types/src/platmap';
import { useWxtiles } from '@mono/use-wxtiles';

import { _WXTilesLayer } from './wxtiles-types';
import { useTimeContext } from '../time-context';
import { isWXTilesLayer } from 'layerTypeUtils';

export const WXTilesServiceContext = createContext<
    PlatmapLayerContext & {
        layers: _WXTilesLayer[] | null;
    }
>({
    layers: null,
    setMap: () => {},
    shuffle: () => {},
});

const getWxtilesLayerIds = (settings: PlatMapSettings) => {
    return settings.layers
        .filter(isWXTilesLayer)
        .map((l) => l.state.wxtilesLayerId)
        .filter((l): l is string => !!l);
};

export const WXTilesServiceContextProvider = ({
    children,
    serviceEndpoint,
    delimiter,
    anchorId,
}: {
    children: JSX.Element;
    serviceEndpoint: string;
    delimiter: string;
    anchorId: string;
}) => {
    const [state, setState] = useState<{
        map: mapboxgl.Map | null;
        shuffleIndex: string;
    }>({
        map: null,
        shuffleIndex: 'The sqrt(-1), characterised as i, was imagined first - discovered in nature second',
    });

    const { selectedTime, setIsLoading } = useTimeContext();
    const { settings } = useSettingsContext();
    const wxtilesService = useWxtiles({
        expandLayers: getWxtilesLayerIds(settings),
        wxtilesServiceUrl: serviceEndpoint,
    });

    useEffect(() => {
        // wxtiles cannot do any clever loading, just set to false and let mapbox manage it
        setIsLoading((prev) => ({ ...prev, wxtiles: false }));
    }, []);

    useEffect(() => {
        if (!state.map || !wxtilesService.layers) return;
        const style = state.map.getStyle();
        if (!style) return;
        const mapboxLayers = style.layers;
        if (!mapboxLayers) return;
        if (!selectedTime) return;

        const existingLayers = mapboxLayers
            .filter((l) => {
                if (!l.id.startsWith(['plat', 'wxtiles', ''].join(delimiter))) return false;
                if (l.id.includes(anchorId)) return false;
                return true;
            })
            .map((a) => a.id);

        const targetLayers = settings.layers
            .filter((l): l is Layer & { state: WXTilesLayer } => {
                if (l.state.type !== 'wxtiles') return false;
                if (!l.state.wxtilesLayerId || !l.state.styleId) return false;
                return true;
            })
            .map((layer) => {
                const wxtilesLayer = wxtilesService.layers.find((l) => l.id === layer.state.wxtilesLayerId);
                if (!wxtilesLayer) return null;
                const layerReady = wxtilesService.expandedLayers.find((l) => l.layerId === layer.state.wxtilesLayerId);

                if (!layerReady) return null;
                const instance = wxtilesLayer.instances[0];
                const time = getBestTimeForLayer(layerReady.times, selectedTime);
                if (!time) return null;

                return ['plat', 'wxtiles', layer.id, layer.state.wxtilesLayerId, layer.state.styleId, instance.id, time].join(delimiter);
            })
            .filter((a): a is string => !!a);

        const { add, remove } = diff(existingLayers, targetLayers);

        for (const layerIndex of remove) {
            state.map.removeLayer(layerIndex);
            state.map.removeSource(layerIndex);
        }

        // This if is a poll to check if the anchor layers have finished being added to mapbox.
        // Better to subscribe and be notified, but mapboxgl doesn't support that. They'd be a lot cooler if they did.
        if (
            add.some((layerIndex) => {
                const [, , layerId] = layerIndex.split(delimiter);
                const anchorIndex = ['plat', 'wxtiles', layerId, anchorId].join(delimiter);

                return !mapboxLayers.find((l) => l.id === anchorIndex);
            })
        ) {
            setTimeout(() => {
                setState((prev) => ({ ...prev, shuffleIndex: prev.shuffleIndex + 'x' }));
            }, 16);
            return;
        }

        for (const layerIndex of add) {
            const [, , layerId, wxtilesLayerId, styleId, , time] = layerIndex.split(delimiter);
            if (!wxtilesLayerId || !styleId) continue;

            const url = wxtilesService.getUrl(wxtilesLayerId, time, styleId);
            if (!url) continue;

            state.map.addSource(layerIndex, {
                type: 'raster',
                tiles: [url],
                scheme: 'tms',
            });

            state.map.addLayer(
                {
                    id: layerIndex,
                    type: 'raster',
                    source: layerIndex,
                    paint: {
                        'raster-fade-duration': 0,
                    },
                },
                ['plat', 'wxtiles', layerId, anchorId].join(delimiter)
            );
        }

        for (const layer of settings.layers) {
            if (layer.state.type !== 'wxtiles') continue;
            const wxtilesLayerIndices = state.map
                .getStyle()
                .layers?.map((l) => l.id)
                .filter((l) => l.startsWith(['plat', 'wxtiles', layer.id].join(delimiter)))
                .filter((l) => !l.includes('anchor'));

            if (!wxtilesLayerIndices) continue;

            const layerAnchorId = ['plat', 'wxtiles', layer.id, anchorId].join(delimiter);
            for (const layerIndex of wxtilesLayerIndices) {
                state.map.moveLayer(layerIndex, layerAnchorId);
            }
        }
    }, [wxtilesService.expandedLayers, state.map, state.shuffleIndex, wxtilesService.layers, selectedTime]);

    return (
        <WXTilesServiceContext.Provider
            value={{
                setMap: (map: mapboxgl.Map) => setState((state) => ({ ...state, map })),
                shuffle: (shuffleIndex: string) => {
                    setState((state) => ({ ...state, shuffleIndex }));
                },
                layers: wxtilesService.layers,
            }}
        >
            {children}
        </WXTilesServiceContext.Provider>
    );
};

// TODO add tests for this
// This function assumes that timesteps are regular.
export const getBestTimeForLayer = (times: string[], time: string) => {
    if (times.length < 2) return null;

    const sortedTimes = times.sort();

    const earliestIndex = sortedTimes.findIndex((t) => t >= time);
    if (earliestIndex === -1) return null;

    if (earliestIndex === sortedTimes.length - 1) return sortedTimes[earliestIndex];

    return sortedTimes[earliestIndex + 1];
};
